import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { Observable } from 'rxjs';
import { UserProfile } from '../interfaces/user-profile.interface';
import { map } from 'rxjs/operators';
import { VersionChipComponent } from '../components/version-chip/version-chip.component';

// Add this interface at the top of the file, after the imports
interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  children?: MenuItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, VersionChipComponent],
  template: `
    <!-- Desktop Sidebar -->
    <div class="sidebar desktop-only">
      <app-version-chip></app-version-chip>
      <div class="logo">
        <img src="assets/images/premier-logo.png" alt="Premier Logo">
      </div>

      <nav class="menu-items">
        <!-- Regular User Menu Items -->
        <ul>
          <li>
            <a routerLink="/dashboard" routerLinkActive="active">
              <i class="fas fa-home"></i>
              Dashboard
            </a>
          </li>
          <li>
            <a routerLink="/time-tracking" routerLinkActive="active">
              <i class="fas fa-clock"></i>
              Time Tracking
            </a>
          </li>
          <li>
            <span class="section-label">Time Off</span>
          </li>
          <li>
            <a routerLink="/time-off" routerLinkActive="active">
              <i class="fas fa-calendar-plus"></i>
              Request Time Off
            </a>
          </li>
          <li>
            <a routerLink="/vacation-calendar" routerLinkActive="active">
              <i class="fas fa-calendar-alt"></i>
              Vacation Calendar
            </a>
          </li>

          <!-- Documents Section -->
          <li>
            <span class="section-label">Documents</span>
          </li>
          <li>
            <a routerLink="/sop" routerLinkActive="active">
              <i class="fas fa-file-alt"></i>
              SOPs
            </a>
          </li>
          <li>
            <a routerLink="/policies" routerLinkActive="active">
              <i class="fas fa-book"></i>
              Policies
            </a>
          </li>
          <li>
            <a routerLink="/documents" routerLinkActive="active">
              <i class="fas fa-folder"></i>
              Documents
            </a>
          </li>

          <!-- Admin Menu Items -->
          <ng-container *ngIf="isAdmin$ | async">
            <li>
              <span class="section-label">Admin</span>
            </li>
            <li>
              <a routerLink="/admin/time-reports" routerLinkActive="active">
                <i class="fas fa-chart-bar"></i>
                Time Reports
              </a>
            </li>
            <li>
              <a routerLink="/admin/time-off-approval" routerLinkActive="active">
                <i class="fas fa-tasks"></i>
                Time Off Approval
              </a>
            </li>
            <li>
              <a routerLink="/admin/users" routerLinkActive="active">
                <i class="fas fa-users"></i>
                User Management
              </a>
            </li>
            <li>
              <a routerLink="/admin/global-settings" routerLinkActive="active">
                <i class="fas fa-cog"></i>
                Global Settings
              </a>
            </li>
          </ng-container>
        </ul>
      </nav>

      <!-- Desktop User Profile Section -->
      <div class="user-profile" *ngIf="user$ | async as user">
        <div class="user-info">
          <span class="user-name">{{ getUserFullName(user) }}</span>
          <a routerLink="/profile" class="profile-link">
            <i class="fas fa-user"></i> Profile
          </a>
          <a (click)="onLogout()" class="logout-link">
            <i class="fas fa-sign-out-alt"></i> Logout
          </a>
        </div>
      </div>
    </div>

    <!-- Mobile Bottom Navigation -->
    <nav class="mobile-nav mobile-only" *ngIf="user$ | async as user">
      <a routerLink="/dashboard" routerLinkActive="active">
        <i class="fas fa-home"></i>
        <span>Home</span>
      </a>
      <a routerLink="/time-tracking" routerLinkActive="active">
        <i class="fas fa-clock"></i>
        <span>Time</span>
      </a>
      <a routerLink="/time-off" routerLinkActive="active">
        <i class="fas fa-calendar"></i>
        <span>Time Off</span>
      </a>
      <a routerLink="/documents" routerLinkActive="active">
        <i class="fas fa-folder"></i>
        <span>Docs</span>
      </a>
      <button class="nav-item" (click)="toggleAdminMenu()">
        <i class="fas fa-ellipsis-h"></i>
        <span>More</span>
      </button>
    </nav>

    <!-- Menu Popup (for all users) -->
    <div class="admin-menu-overlay" *ngIf="showAdminMenu" (click)="toggleAdminMenu()">
      <div class="admin-menu" (click)="$event.stopPropagation()">
        <div class="admin-menu-header">
          <h3>Menu</h3>
          <button class="close-btn" (click)="toggleAdminMenu()">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="admin-menu-content">
          <!-- User Options - Available for all users -->
          <a routerLink="/profile" routerLinkActive="active" (click)="toggleAdminMenu()">
            <i class="fas fa-user"></i>
            <span>Profile</span>
          </a>
          <a routerLink="/schedule-settings" routerLinkActive="active" (click)="toggleAdminMenu()">
            <i class="fas fa-calendar-alt"></i>
            <span>Work Schedule</span>
          </a>

          <!-- Admin Section - Only visible for admin users -->
          <ng-container *ngIf="isAdmin$ | async">
            <div class="menu-divider">Admin</div>
            <a routerLink="/admin/time-reports" routerLinkActive="active" (click)="toggleAdminMenu()">
              <i class="fas fa-chart-bar"></i>
              <span>Time Reports</span>
            </a>
            <a routerLink="/admin/time-off-approval" routerLinkActive="active" (click)="toggleAdminMenu()">
              <i class="fas fa-tasks"></i>
              <span>Time Off Approval</span>
            </a>
            <a routerLink="/admin/users" routerLinkActive="active" (click)="toggleAdminMenu()">
              <i class="fas fa-users"></i>
              <span>User Management</span>
            </a>
            <a routerLink="/admin/global-settings" routerLinkActive="active" (click)="toggleAdminMenu()">
              <i class="fas fa-cog"></i>
              <span>Global Settings</span>
            </a>
          </ng-container>

          <div class="menu-divider"></div>
          <a (click)="onLogout()" class="logout-option">
            <i class="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </a>
        </div>
      </div>
    </div>
  `,
  styles: [`
    /* Desktop Styles */
    @media (min-width: 769px) {
      .desktop-only {
        display: flex;
      }

      .mobile-only {
        display: none !important;
      }
    }

    /* Mobile Styles */
    @media (max-width: 768px) {
      .desktop-only {
        display: none !important;
      }

      .mobile-only {
        display: flex;
      }
    }

    .sidebar {
      width: 250px;
      background-color: #ffffff;
      height: 100vh;
      display: flex;
      flex-direction: column;
      border-right: 1px solid #e0e0e0;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 1000;
    }

    /* Mobile Bottom Navigation */
    .mobile-nav {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      display: none;
      justify-content: space-around;
      align-items: center;
      padding: 0.5rem;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
      z-index: 1000;
    }

    @media (max-width: 768px) {
      .mobile-nav {
        display: flex;
      }
    }

    .logo {
      padding: 1rem;
      text-align: center;
      border-bottom: 1px solid #e0e0e0;
      margin-top: -0.5rem;
    }

    .logo img {
      max-width: 150px;
      height: auto;
    }

    .menu-items {
      flex: 1;
      overflow-y: auto;
      padding: 1rem 0;
    }

    .menu-items ul {
      list-style: none;
      padding: 0;
      margin: 0;
      width: 100%;
    }

    .menu-items li {
      margin: 0.5rem 0;
    }

    .menu-items a {
      display: flex;
      align-items: center;
      padding: 0.75rem 1.5rem;
      color: #333;
      text-decoration: none;
      transition: all 0.2s ease;
      gap: 0.75rem;
    }

    .menu-items a:hover {
      background-color: rgba(2, 48, 89, 0.05);
    }

    .menu-items a.active {
      background-color: rgba(2, 48, 89, 0.1);
      color: #023059;
      font-weight: 500;
    }

    .menu-divider {
      padding: 0.75rem 1.5rem;
      color: #666;
      font-size: 0.875rem;
      font-weight: 500;
      margin: 1rem 0;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-top: 1px solid #e0e0e0;
      border-bottom: 1px solid #e0e0e0;
      width: 100%;
      box-sizing: border-box;
    }

    .user-profile {
      padding: 1rem 1.5rem;
      border-top: 1px solid #e0e0e0;
      background: #f8f9fa;
    }

    .user-info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .user-name {
      font-weight: 500;
      color: #333;
    }

    .profile-link, .logout-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      text-decoration: none;
      padding: 0.5rem 0;
      cursor: pointer;
      transition: color 0.2s ease;
    }

    .profile-link:hover, .logout-link:hover {
      color: #023059;
    }

    /* Mobile Bottom Navigation */
    .mobile-nav {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      display: flex;
      justify-content: space-around;
      align-items: center;
      padding: 0.5rem;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
      z-index: 1000;
    }

    .mobile-nav a, .mobile-nav .nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.25rem;
      color: #666;
      text-decoration: none;
      padding: 0.5rem;
      min-width: 60px;
      border: none;
      background: none;
      font-size: 0.75rem;
      cursor: pointer;
    }

    .mobile-nav a.active {
      color: #023059;
    }

    .mobile-nav i {
      font-size: 1.25rem;
    }

    /* Admin Menu Popup */
    .admin-menu-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: flex-end;
      justify-content: center;
      z-index: 1001;
      animation: fadeIn 0.2s ease-out;
    }

    .admin-menu {
      background: white;
      width: 100%;
      max-width: 500px;
      border-radius: 16px 16px 0 0;
      padding: 1rem;
      animation: slideUp 0.3s ease-out;
    }

    .admin-menu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 1rem;
      border-bottom: 1px solid #eee;
      margin-bottom: 1rem;
    }

    .admin-menu-header h3 {
      margin: 0;
      color: #333;
      font-size: 1.1rem;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 1.25rem;
      color: #666;
      padding: 0.5rem;
      cursor: pointer;
    }

    .admin-menu-content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .admin-menu-content a {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      color: #333;
      text-decoration: none;
      border-radius: 8px;
      transition: background-color 0.2s ease;
    }

    .admin-menu-content a:hover {
      background-color: #f5f5f5;
    }

    .admin-menu-content i {
      font-size: 1.1rem;
      width: 24px;
      text-align: center;
    }

    .menu-divider {
      height: 1px;
      background: #eee;
      margin: 0.5rem 0;
    }

    .logout-option {
      color: #dc3545 !important;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    /* iPhone Safe Area Support */
    @supports (padding: max(0px)) {
      .mobile-nav {
        padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
        height: calc(60px + env(safe-area-inset-bottom, 0px));
      }

      .admin-menu {
        padding-bottom: max(1rem, env(safe-area-inset-bottom));
      }
    }

    /* Add new section-label style */
    .section-label {
      display: block;
      padding: 0.75rem 1.5rem;
      color: #666;
      font-size: 0.875rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  `]
})
export class SidebarComponent implements OnInit {
  @Input() isMobileMenuOpen = false;
  @Output() closeMobileMenu = new EventEmitter<void>();
  
  user$: Observable<UserProfile | null>;
  isAdmin$: Observable<boolean>;
  showAdminMenu = false;

  constructor(private authService: AuthService) {
    this.user$ = this.authService.user$;
    this.isAdmin$ = this.authService.user$.pipe(
      map(user => user?.roles?.includes('admin') || false)
    );
  }

  ngOnInit(): void {}

  toggleAdminMenu(): void {
    this.showAdminMenu = !this.showAdminMenu;
  }

  async onLogout(): Promise<void> {
    try {
      await this.authService.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  getUserFullName(user: UserProfile): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  }
}